import React, { useEffect, useMemo, useState } from "react";
import { FALLBACKS } from "../mock";

const withParams = (url, params) =>
  !url ? url : url.includes("?") ? url : `${url}?${params}`;

/**
 * Tries the original scraped asset first (hrdmimarlik.com). If it does not
 * resolve within `timeout` ms, or errors, a curated architectural photograph
 * is used instead.
 */
export default function SmartImage({
  src,
  alt,
  className = "",
  fallback,
  fallbackIndex = 0,
  timeout = 1500,
  params = "w=1400&q=80&auto=format&fit=crop",
}) {
  const fb = useMemo(
    () => withParams(fallback || FALLBACKS[fallbackIndex % FALLBACKS.length], params),
    [fallback, fallbackIndex, params]
  );

  const [resolved, setResolved] = useState(null);

  useEffect(() => {
    if (!src) {
      setResolved(fb);
      return;
    }
    // uploaded / imported assets are trusted: no timeout race, only onError
    if (src.includes("/api/files/")) {
      setResolved(src);
      return;
    }
    let cancelled = false;
    setResolved(null);

    const img = new Image();
    const timer = setTimeout(() => {
      if (!cancelled) setResolved(fb);
    }, timeout);

    img.onload = () => {
      if (cancelled) return;
      clearTimeout(timer);
      setResolved(src);
    };
    img.onerror = () => {
      if (cancelled) return;
      clearTimeout(timer);
      setResolved(fb);
    };
    img.src = src;

    return () => {
      cancelled = true;
      clearTimeout(timer);
      img.onload = null;
      img.onerror = null;
    };
  }, [src, fb, timeout]);

  if (!resolved) {
    return <div className={`${className} bg-[#EFECE6] animate-pulse`} aria-label={alt} />;
  }

  return (
    <img
      src={resolved}
      alt={alt}
      onError={() => resolved !== fb && setResolved(fb)}
      className={className}
    />
  );
}
